import type { ProspectMatterType, ProspectPracticeArea } from '@common/enums';
import { getIntakeFormTranslations } from '@common/i18n/intakeForm';
import '@fontsource/dancing-script/600.css';
import type { Contact } from '@common/types/contacts';
import { parseDraftFormResponse } from '@common/types/formResponseParsing';
import type { ContactFormValues, FormSubmissionResponse, GetDraftFormSubmissionBody } from '@common/types/formSubmissions';
import type { TemplateFormField } from '@common/types/templateFormFields';
import type { FormGroup, TemplateFormMetadata } from '@common/types/templateForms';
import type { DraftFormSubmissionRequest, FormSubmissionRequest } from '@common/types/workflows';
import { resolveFormGroups } from '@common/utils/formGroups';
import { isValidEmail } from '@common/utils/isValidEmail';
import { getVisibleQuestions } from '@common/utils/questionVisibility';
import { Box, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { isAxiosError } from 'axios';
import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ActivityTracker from '@/components/ActivityTracker';
import FormComponent from '@/components/FormComponent/FormComponent';
import ClientViewWrapper from '@/pages/QuoteIntakeClientView/ClientViewWrapper';
import {
    clientSideDraftIntakeFormSubmit,
    clientSideIntakeOnlyFormSubmit,
    clientSideOrchestrationFormSubmit,
    clientSidePostIntakeSubmission,
    clientSideSilentDraftIntakeFormSave,
    getClientPostSubmittedIntakeFormData,
    getClientSideIntakeForms,
    getIntakeOnlyFormStatus,
    getPostIntakeFormStatus,
    verifyClientForDraftForm,
} from '@/services/ClientSideTemplateFormService';
import { getIntakeFormStatus } from '@/services/EmailApprovalService';
import { getIntakePreFillData } from '@/services/ProspectService';
import { reportFormGroupMismatches } from '@/utils/formGroupMonitoring';
import { applyTagPrefill, buildDefaultValuesFromQuote, buildPrefillByTag, parseIntakePrefillResponse } from '@/utils/intake';
import { isRetryableClientLoadError } from '@/utils/queryError';
import { errorSnackbar, successSnackbar, warningSnackbar } from '@/utils/snackbar';
import ButtonSection from '../ButtonSection';
import ClientErrorPage from '../ClientErrorPage';
import { isBlockingIntakeStatus } from '../constants';
import IntakeFormStepper from '../IntakeFormStepper';
import { mainWrapperStyles } from '../styles';
import { getDuplicateEmailIndices, hasDuplicateContactEmails, scrollToError, syncDuplicateContactEmailErrors } from '../utils';
import VerifyEmailDialog from '../VerifyEmailDialog';
import { contactToFormValues, hydrateContactFormValues } from './contactHydration';
import ContactInfoSection from './ContactInfoSection';
import { type ContactBlockEntryErrors, hasContactFormErrors, validateContactBlock, validateContactEntry } from './contactValidation';
import { isEmailField, validateFieldValue } from './fieldValidation';
import FinancialSummary from './FinancialSummary';
import FormHeaderSection from './FormHeaderSection';
import { useEntryModeStatus } from './hooks/useEntryModeStatus';
import { useFormLock } from './hooks/useFormLock';
import { useRetainerFlow } from './hooks/useRetainerFlow';
import { useSilentDraftAutosave } from './hooks/useSilentDraftAutosave';
import LegalDocumentModal from './LegalDocumentModal';
import RetainerAcceptanceTable from './RetainerAcceptanceTable';
import SectionDescription from './SectionDescription';
import { ACTIVITY_TRACKER_TIMEOUT_MS, ACTIVITY_TRACKER_WARNING_TIME_MS } from './timingConfig';
import {
    buildTemplateFormFields,
    deriveFormEntryMode,
    extractApiError,
    getApiErrorLanguage,
    getApiErrorMessage,
    getApiErrorStatus,
    getProspectId,
    groupTemplateFormFields,
    initFormValuesFromDraft,
    initFormValuesFromSubmission,
    requiresEmailVerification,
    setFormValue,
    toContactFormValuesApi,
    toFormValueString,
    isEmptyRequiredFieldValue,
} from './types';

function scrollToFirstError() {
    scrollToError()?.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
}

interface Props {
    practiceArea: ProspectPracticeArea;
    matterType: ProspectMatterType;
}

export default function IntakeFormViewV2({ practiceArea, matterType }: Props) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const wrapperElementId = useId();
    const { intakeTemplateId, lawFirmId } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    // Derive the form entry mode from URL params - this determines the entire flow
    const entryMode = useMemo(() => deriveFormEntryMode(searchParams), [searchParams]);

    // Additional URL params not part of entry mode
    const quoteTemplateId = Number(searchParams.get('quoteTemplateId')) || undefined;

    // Convenience accessors for common values (derived from entryMode)
    const prospectId = getProspectId(entryMode);
    const instanceId = searchParams.get('instanceId');
    const draftFormSubmissionId = entryMode.type === 'draft' ? entryMode.draftFormSubmissionId : null;
    const formSubmissionId = entryMode.type === 'postIntake' ? entryMode.formSubmissionId : null;
    const prospectVersionId = entryMode.type === 'postIntake' ? entryMode.prospectVersionId : null;

    const [draftSubmissionId, setDraftSubmissionId] = useState<string>(() => draftFormSubmissionId ?? '');
    const effectiveDraftFormSubmissionId = draftSubmissionId || draftFormSubmissionId || undefined;

    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [canRetryError, setCanRetryError] = useState(false);

    const [headerDescription, setHeaderDescription] = useState('');
    const [footerTemplate, setFooterTemplate] = useState('');
    const [templateMetadata, setTemplateMetadata] = useState<TemplateFormMetadata | null>(null);

    const [intakeFormLoading, setIntakeFormLoading] = useState(false);

    const [steps, setSteps] = useState<string[]>([]);
    const [formGroups, setFormGroups] = useState<FormGroup[]>([]);
    const [currentStep, setCurrentStep] = useState(0);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const isFinalStep = useMemo(() => currentStep === steps.length - 1, [steps, currentStep]);

    const currentStepGroupDescription = useMemo(() => {
        const stepName = steps[currentStep];
        if (!stepName) return null;
        return formGroups.find((group) => group.name === stepName)?.description ?? null;
    }, [formGroups, steps, currentStep]);

    const sectionDescriptionSx = { margin: isMobile ? '0 8px' : '0 24px' };

    const [groupedFields, setGroupedFields] = useState<Record<string, TemplateFormField[]>>({});
    const [fieldErrors, setFieldErrors] = useState<Record<string, string | null>>({});
    // Per-entry errors for contact block questions, keyed by templateFormFieldId
    const [contactBlockErrors, setContactBlockErrors] = useState<Record<string, ContactBlockEntryErrors[]>>({});
    const [formValues, setFormValues] = useState<Record<string, string | null>>({});
    const [prefillData, setPrefillData] = useState<Record<string, string>>({});
    const [contactResponses, setContactResponses] = useState<Contact[]>([]);

    // Contact info form state - separate from template form fields
    const [contactFormValues, setContactFormValues] = useState<ContactFormValues[]>([]);
    const [contactFormErrors, setContactFormErrors] = useState<Partial<Record<keyof ContactFormValues, string>>[]>([]);
    const [expandedContactSections, setExpandedContactSections] = useState<Record<number, boolean>>({});
    // Stores contact form values from draft submissions for merging with contactResponses
    const [savedContactFormValues, setSavedContactFormValues] = useState<ContactFormValues[]>([]);
    const { intakeFormStatus, setIntakeFormStatus, statusLanguage, setStatusLanguage, validateStatusAndUpdate } = useEntryModeStatus(entryMode);
    // Prefer the loaded template's language; fall back to the language reported by
    // the status endpoints so pre-verification UI is localized too.
    const language = templateMetadata?.language ?? statusLanguage ?? 'en';
    const t = useMemo(() => getIntakeFormTranslations(language), [language]);
    const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
    const [clientEmail, setClientEmail] = useState('');
    const [verifyEmail, setVerifyEmail] = useState('');
    const [verifyOpen, setVerifyOpen] = useState(false);
    const [postIntakeData, setPostIntakeData] = useState<FormSubmissionResponse | null>(null);

    const [isDraftFormSubmitted, setIsDraftFormSubmitted] = useState<boolean>(false);
    const [inactiveTimerReached, setInactiveTimerReached] = useState(false);
    const [inactiveSaveFailed, setInactiveSaveFailed] = useState(false);

    // Resolved prospectId for locking - may come from URL or from fetched postIntakeData
    const { lockInfo, contactId, acquireLock, releaseLock } = useFormLock({
        prospectId: prospectId || postIntakeData?.prospectId || null,
        prospectVersionId,
        shouldExtendLock: !isSubmitted && !isDraftFormSubmitted,
        language,
    });
    const resolvedProspectId = prospectId || postIntakeData?.prospectId || lockInfo?.prospectId || null;

    const isDraftSubmitEnabled = useMemo(() => {
        if (contactFormValues.length === 0) return false;
        const hasValidContacts = contactFormValues.every((contact) => isValidEmail(contact.email) && contact.firstName.trim() !== '' && contact.lastName.trim() !== '');
        if (!hasValidContacts) return false;
        return !hasDuplicateContactEmails(contactFormValues.map((contact) => contact.email));
    }, [contactFormValues]);
    const primaryContactEmail = contactFormValues[0]?.email?.trim() ?? '';
    const resolvedClientEmail = lockInfo?.lockedByEmail || clientEmail || primaryContactEmail;

    const areAllRequiredFieldsFilled = useMemo(() => {
        const visibleFields = Object.values(groupedFields)
            .flat()
            .filter((field) => field.isVisibleToUser);

        return visibleFields.every((field) => {
            const value = formValues[field.templateFormFieldId];
            if (field.questionSchema.type === 'contactInfoSection') {
                return validateContactBlock(value, field.questionSchema, !!field.questionValidationSchema?.required, t, language).isValid;
            }
            if (field.questionValidationSchema?.required !== true) return true;
            return !isEmptyRequiredFieldValue(value, field.questionSchema.type, field.questionSchema.columns, field.questionSchema.rowCount);
        });
    }, [groupedFields, formValues, t, language]);

    const [isModalVerified, setIsModalVerified] = useState<boolean>(false);
    const [loading, setLoading] = useState(false);
    const [textLoading, setTextLoading] = useState(true);

    const { retainerResponse, showLegalDocument, closeLegalDocument, clientName, areAllRetainersSigned, fetchRetainerDetails, checkRetainerRequired } = useRetainerFlow({
        prospectId: resolvedProspectId,
        contactId,
    });

    // Centralized handler for form field value changes
    const handleFieldValueChange = useCallback(
        (field: TemplateFormField, value: unknown) => {
            const fieldId = field.templateFormFieldId;
            setHasUnsavedChanges(true);
            setFormValues((prev) => setFormValue(prev, fieldId, value));

            // Contact blocks re-validate as the user edits so fixed fields clear while others stay flagged
            if (field.questionSchema.type === 'contactInfoSection') {
                if (!contactBlockErrors[fieldId]) return;
                const result = validateContactBlock(toFormValueString(value), field.questionSchema, !!field.questionValidationSchema?.required, t, language);
                setContactBlockErrors((prev) => ({ ...prev, [fieldId]: result.entryErrors }));
                setFieldErrors((prev) => ({ ...prev, [fieldId]: result.isValid ? null : t.contactInfo.validation.contactBlockHasErrors }));
                return;
            }

            // Clear field error when user starts typing
            if (fieldErrors[fieldId]) {
                setFieldErrors((prev) => ({ ...prev, [fieldId]: null }));
            }
        },
        [fieldErrors, contactBlockErrors, t, language],
    );

    // Handler for contact info section field changes
    const handleContactFormChange = useCallback(
        (contactIndex: number, field: keyof ContactFormValues, value: unknown) => {
            setHasUnsavedChanges(true);
            setContactFormValues((prev) => {
                const updated = [...prev];
                if (updated[contactIndex]) {
                    updated[contactIndex] = { ...updated[contactIndex], [field]: value };
                }

                if (field === 'email') {
                    setContactFormErrors((prevErrors) =>
                        syncDuplicateContactEmailErrors(
                            updated.map((contact) => contact.email),
                            prevErrors,
                            t.contactInfo.validation.duplicateEmail,
                        ),
                    );
                } else if (contactFormErrors[contactIndex]?.[field]) {
                    setContactFormErrors((prevErrors) => {
                        const nextErrors = [...prevErrors];
                        if (nextErrors[contactIndex]) {
                            nextErrors[contactIndex] = { ...nextErrors[contactIndex], [field]: undefined };
                        }
                        return nextErrors;
                    });
                }

                return updated;
            });
        },
        [contactFormErrors, t.contactInfo.validation.duplicateEmail],
    );

    const getVisibleFieldsForStep = useCallback(
        (stepIndex: number) => getVisibleQuestions(groupedFields[steps[stepIndex]] ?? [], (field) => formValues[field.templateFormFieldId]),
        [groupedFields, steps, formValues],
    );

    const applyDuplicateContactEmailErrors = useCallback(() => {
        const emails = contactFormValues.map((contact) => contact.email);
        const duplicateIndices = getDuplicateEmailIndices(emails);

        setContactFormErrors((prev) => syncDuplicateContactEmailErrors(emails, prev, t.contactInfo.validation.duplicateEmail));

        if (duplicateIndices.length === 0) {
            return { isValid: true, contactErrorIndices: [] as number[] };
        }

        return { isValid: false, contactErrorIndices: duplicateIndices };
    }, [contactFormValues, t.contactInfo.validation.duplicateEmail]);

    const validateContactInfoStep = useCallback(() => {
        let isValid = true;
        let hasInvalidEmail = false;
        const hiddenFields = templateMetadata?.hiddenContactFields;
        const requiredFields = templateMetadata?.requiredContactFields;
        const { fieldRequired, invalidEmail, duplicateEmail } = t.contactInfo.validation;

        const nextErrors = contactFormValues.map((contact) => {
            const result = validateContactEntry(contact, { hiddenFields, requiredFields }, { fieldRequired, invalidEmail });
            if (hasContactFormErrors(result.errors)) isValid = false;
            hasInvalidEmail = hasInvalidEmail || result.hasInvalidEmail;
            return result.errors;
        });

        const duplicateIndices = new Set(getDuplicateEmailIndices(contactFormValues.map((contact) => contact.email)));
        let hasDuplicateEmail = false;
        if (duplicateIndices.size > 0) {
            hasDuplicateEmail = true;
            isValid = false;
            duplicateIndices.forEach((index) => {
                nextErrors[index] = { ...nextErrors[index], email: duplicateEmail };
            });
        }

        setContactFormErrors(nextErrors);
        const contactErrorIndices = nextErrors.flatMap((errors, index) => (hasContactFormErrors(errors) ? [index] : []));
        return { isValid, hasInvalidEmail, hasDuplicateEmail, contactErrorIndices };
    }, [contactFormValues, templateMetadata, t.contactInfo]);

    const validateTemplateFieldsForCurrentStep = useCallback(() => {
        let isValid = true;
        let hasInvalidEmail = false;
        const nextErrors: Record<string, string | null> = { ...fieldErrors };
        const nextBlockErrors: Record<string, ContactBlockEntryErrors[]> = { ...contactBlockErrors };
        const visibleFields = getVisibleFieldsForStep(currentStep);
        const visibleFieldIds = new Set(visibleFields.map((field) => field.templateFormFieldId));

        for (const field of groupedFields[steps[currentStep]] ?? []) {
            if (!visibleFieldIds.has(field.templateFormFieldId)) {
                nextErrors[field.templateFormFieldId] = null;
                delete nextBlockErrors[field.templateFormFieldId];
                continue;
            }

            const fieldId = field.templateFormFieldId;

            if (field.questionSchema.type === 'contactInfoSection') {
                const result = validateContactBlock(formValues[fieldId], field.questionSchema, !!field.questionValidationSchema?.required, t, language);
                nextBlockErrors[fieldId] = result.entryErrors;
                nextErrors[fieldId] = result.isValid ? null : t.contactInfo.validation.contactBlockHasErrors;
                isValid = isValid && result.isValid;
                hasInvalidEmail = hasInvalidEmail || result.hasInvalidEmail;
                continue;
            }

            const value = formValues[fieldId] ?? field.questionSchema.value ?? '';
            const error = validateFieldValue(field, value, t.formValidation, language);

            if (error) {
                isValid = false;
                if (isEmailField(field)) {
                    hasInvalidEmail = hasInvalidEmail || error !== t.formValidation.fieldRequired;
                }
            }

            nextErrors[fieldId] = error;
        }

        setFieldErrors(nextErrors);
        setContactBlockErrors(nextBlockErrors);
        return { isValid, hasInvalidEmail };
    }, [currentStep, fieldErrors, contactBlockErrors, formValues, getVisibleFieldsForStep, groupedFields, steps, t, language]);

    const validateCurrentStep = useCallback(() => {
        const contactValidation = currentStep === 0 ? validateContactInfoStep() : { isValid: true, hasInvalidEmail: false, hasDuplicateEmail: false, contactErrorIndices: [] };
        const templateValidation = validateTemplateFieldsForCurrentStep();
        const isValid = contactValidation.isValid && templateValidation.isValid;
        const hasInvalidEmail = contactValidation.hasInvalidEmail || templateValidation.hasInvalidEmail;

        if (!isValid) {
            if (contactValidation.contactErrorIndices.length > 0) {
                setExpandedContactSections((prev) => {
                    const next = { ...prev };
                    for (const index of contactValidation.contactErrorIndices) {
                        next[index] = true;
                    }
                    return next;
                });
            }
            window.setTimeout(scrollToFirstError, 500);
            if (contactValidation.hasDuplicateEmail) {
                warningSnackbar(t.contactInfo.validation.duplicateEmailSnackbar, true);
            } else {
                warningSnackbar(
                    hasInvalidEmail && currentStep === 0
                        ? t.contactInfo.validation.invalidEmailSnackbar
                        : currentStep === 0
                          ? t.contactInfo.validation.fillRequiredFields
                          : hasInvalidEmail
                            ? t.formValidation.invalidEmailSnackbar
                            : t.formValidation.fillRequiredFields,
                    true,
                );
            }
        }

        return isValid;
    }, [currentStep, validateContactInfoStep, validateTemplateFieldsForCurrentStep, t.contactInfo, t.formValidation]);

    /**
     * Builds contactFormValues (step 0) from API data. Unlike V1 — where contact fields live inside
     * template form fields and post-intake has a separate applyPostIntakeContactHydration pass — V2
     * keeps contacts in this dedicated array and uses one merge for any load that includes a snapshot.
     *
     * Branch → when it runs:
     * - savedContactFormValues.length > 0 → draft revisit (handleVerifyClientDraft) or post-intake
     *   (handleVerifyClientPostIntake): API returns snapshot + contactResponses; merged by
     *   hydrateContactFormValues (live contact row wins identity, paired by contactId).
     * - contactResponses only → quote/manual prefill (fetchAndSetIntakeFormData): no snapshot yet.
     * - neither → fresh intake: one empty contact section.
     *
     * Either way each slot carries its party's contactId, so saves and submits send it back.
     */
    useEffect(() => {
        if (savedContactFormValues.length > 0) {
            const initialValues = hydrateContactFormValues(savedContactFormValues, contactResponses);
            setContactFormValues(initialValues);
            setContactFormErrors(initialValues.map(() => ({})));
        } else if (contactResponses.length > 0) {
            const initialValues = contactResponses.map((contact) => contactToFormValues(contact));
            setContactFormValues(initialValues);
            setContactFormErrors(initialValues.map(() => ({})));
        } else {
            // Fresh form with no contacts - create one empty contact section
            setContactFormValues([
                {
                    firstName: '',
                    lastName: '',
                    email: '',
                    cellPhone: '',
                },
            ]);
            setContactFormErrors([{}]);
        }
    }, [contactResponses, savedContactFormValues]);

    // Build the URL for re-accessing the intake form after draft submission
    const intakeUrl = useMemo(() => {
        const base = `/intake/view/${intakeTemplateId}/${lawFirmId}`;
        const params = new URLSearchParams();

        if (effectiveDraftFormSubmissionId) {
            params.set('draftFormSubmissionId', effectiveDraftFormSubmissionId);
        }
        if (quoteTemplateId) {
            params.set('quoteTemplateId', `${quoteTemplateId}`);
        }

        // Add entry mode-specific params
        if (entryMode.type === 'manualProspect') {
            params.set('instanceId', entryMode.instanceId);
            params.set('prospectId', entryMode.prospectId);
        } else if (entryMode.type === 'draft' && entryMode.prospectId) {
            params.set('prospectId', entryMode.prospectId);
        }

        return `${base}?${params}`;
    }, [intakeTemplateId, lawFirmId, effectiveDraftFormSubmissionId, quoteTemplateId, entryMode]);

    const handleBack = () => {
        if (currentStep > 0) {
            setCurrentStep((prev) => prev - 1);
            document.getElementById(wrapperElementId)?.scrollIntoView({ behavior: 'instant' });
        }
    };

    const handleNext = () => {
        if (!isFinalStep && validateCurrentStep()) {
            setCurrentStep((prev) => prev + 1);
            document.getElementById(wrapperElementId)?.scrollIntoView({ behavior: 'instant' });
        }
    };

    const buildDraftPayload = (autosaveDraftFormSubmissionId?: string): DraftFormSubmissionRequest | null => {
        if (!resolvedClientEmail || !intakeTemplateId) return null;

        return {
            clientEmail: resolvedClientEmail,
            submittedBy: resolvedClientEmail,
            templateFormId: intakeTemplateId,
            templateFormFields: buildTemplateFormFields(formValues, groupedFields),
            contactFormValues: contactFormValues.map(toContactFormValuesApi),
            draftFormSubmissionId: autosaveDraftFormSubmissionId || effectiveDraftFormSubmissionId,
            prospectId: prospectId || undefined,
            instanceId: instanceId || undefined,
            contacts: contactResponses?.length > 1 ? contactResponses : undefined,
        };
    };

    const handleDraftSubmit = async () => {
        const duplicateEmailValidation = applyDuplicateContactEmailErrors();
        if (!duplicateEmailValidation.isValid) {
            setExpandedContactSections((prev) => {
                const next = { ...prev };
                for (const index of duplicateEmailValidation.contactErrorIndices) {
                    next[index] = true;
                }
                return next;
            });
            window.setTimeout(scrollToFirstError, 500);
            warningSnackbar(t.contactInfo.validation.duplicateEmailSnackbar, true);
            return;
        }

        // Validate status based on entry mode before saving draft
        const isValid = await validateStatusAndUpdate();
        if (!isValid) {
            setVerifyOpen(false);
            return;
        }

        if (loading) return;

        const payload = buildDraftPayload(effectiveDraftFormSubmissionId);
        if (!payload) return;

        setIsModalVerified(true);
        setLoading(true);
        try {
            const resp = await clientSideDraftIntakeFormSubmit(payload);
            setClientEmail('');
            setIsDraftFormSubmitted(true);
            setDraftSubmissionId(resp.data.DraftFormSubmissionId);
            setHasUnsavedChanges(false);
            setIsModalVerified(false);
            setIsSubmitted(true);
            if (contactResponses?.length > 0) {
                releaseLock();
            }
        } catch (error: unknown) {
            console.error('Draft submit failed:', error);
        } finally {
            setLoading(false);
        }
    };

    const saveDraftForInactivity = async () => {
        const payload = buildDraftPayload(effectiveDraftFormSubmissionId);
        if (!payload) {
            warningSnackbar(t.notifications.autoSaveDraftFailed, true);
            return false;
        }

        setLoading(true);
        try {
            const response = await clientSideSilentDraftIntakeFormSave({
                ...payload,
                shouldUpsertDraft: isDraftSubmitEnabled,
            });
            if (response.data.DraftFormSubmissionId) {
                setDraftSubmissionId(response.data.DraftFormSubmissionId);
                setHasUnsavedChanges(false);
                return true;
            }
            return false;
        } catch (error: unknown) {
            console.error('Silent draft save on inactivity failed:', error);
            errorSnackbar(t.notifications.autoSaveDraftFailed, true);
            return false;
        } finally {
            setLoading(false);
        }
    };

    const handleInactivityTimeout = async () => {
        try {
            if (!isDraftFormSubmitted && prospectId && isDraftSubmitEnabled) {
                await handleDraftSubmit();
                return;
            }

            if (formSubmissionId && prospectVersionId && areAllRequiredFieldsFilled) {
                await handleSubmit();
                setIsSubmitted(true);
                return;
            }

            if (formSubmissionId && prospectVersionId) {
                setInactiveTimerReached(true);
                setIsSubmitted(true);
                return;
            }

            const saved = await saveDraftForInactivity();
            if (!saved) {
                setInactiveSaveFailed(true);
                setInactiveTimerReached(true);
                setIsSubmitted(true);
                return;
            }

            setInactiveSaveFailed(false);
            setInactiveTimerReached(true);
            setIsSubmitted(true);
        } finally {
            await releaseLock();
        }
    };

    const isIntakeFormActive = !isBlockingIntakeStatus(intakeFormStatus?.Status);

    useSilentDraftAutosave({
        enabled: entryMode.type !== 'postIntake' && !verifyOpen && !isSubmitted && isIntakeFormActive && !loading,
        shouldUpsertDraft: isDraftSubmitEnabled,
        initialDraftFormSubmissionId: effectiveDraftFormSubmissionId,
        buildPayload: buildDraftPayload,
        onDraftFormSubmissionIdChange: setDraftSubmissionId,
        isBlocked: loading,
    });

    const handleLegalDocumentComplete = async () => {
        closeLegalDocument();
        setLoading(true);
        try {
            await fetchRetainerDetails(resolvedProspectId);
            if (entryMode.type === 'postIntake') {
                await getPostSubmittedIntakeForm();
            } else if (draftFormSubmissionId) {
                await handleVerifyClientDraft({
                    draftFormSubmissionId: draftFormSubmissionId,
                    clientEmail: verifyEmail,
                });
            } else if (prospectId) {
                setVerifyOpen(false);
                await fetchAndSetIntakeFormData(prospectId);
            }
        } catch (error: unknown) {
            console.error('Legal document completion failed:', error);
        } finally {
            setLoading(false);
        }
    };

    //This function is to verify the client for draft form when retainer is available
    const handleVerifyClientDraft = async (payload: GetDraftFormSubmissionBody) => {
        try {
            const resp = await verifyClientForDraftForm(payload);
            const draftFormResp = parseDraftFormResponse(resp.data.formResponse);
            // Store draft contact form values first - these will be merged with contactResponses in useEffect
            setSavedContactFormValues(resp.data.contactFormValues ?? []);
            setContactResponses(resp?.data?.contactResponses ?? []);
            setClientEmail(resp.data.clientEmail);
            // Initialize form values from draft data
            setFormValues(initFormValuesFromDraft(draftFormResp));
            setVerifyOpen(false);

            if (resp.status === 200) {
                await fetchIntakeTemplateData();
            }
        } catch (error: unknown) {
            const apiError = extractApiError(error);
            if (apiError.status === 403) {
                errorSnackbar(t.notifications.draftAlreadySubmitted);
            } else if (apiError.status === 404 || apiError.status === 400) {
                errorSnackbar(t.notifications.emailNotRecognized);
            } else {
                errorSnackbar(t.notifications.somethingWentWrong, true);
            }
            console.error('Verify client draft failed:', error);
        } finally {
            setLoading(false);
        }
    };

    //This function is to verify the client for draft form
    const handleVerifyClientOnRevisit = async (checkForLocking: boolean) => {
        const isValid = await validateStatusAndUpdate();
        if (!isValid) {
            setVerifyOpen(false);
            return;
        }

        const payload = {
            draftFormSubmissionId: draftFormSubmissionId as string,
            clientEmail: verifyEmail,
        };
        setIsModalVerified(true);
        setLoading(true);

        if (checkForLocking) {
            const lockResult = await acquireLock(verifyEmail);
            if (!lockResult.success) {
                setLoading(false);
                return;
            }

            const retainerResult = await checkRetainerRequired(lockResult.lockInfo, lockResult.contactId);
            if (retainerResult.requiresSignature) {
                setVerifyOpen(false);
                setLoading(false);
                return;
            }
        }
        await handleVerifyClientDraft(payload);
    };

    //API call to verify client and acquire lock for intake form
    const getContactIdForIntakeForm = async () => {
        if (instanceId && prospectId) {
            try {
                const intakeStatusResp = await getIntakeFormStatus(instanceId, prospectId);
                if (intakeStatusResp?.data?.Language) {
                    setStatusLanguage(intakeStatusResp.data.Language);
                }
                if (intakeStatusResp?.data?.DraftFormSubmissionId) {
                    const intakeUrl = `/intake/view/${intakeTemplateId}/${lawFirmId}?instanceId=${instanceId}&prospectId=${prospectId}&draftFormSubmissionId=${intakeStatusResp?.data?.DraftFormSubmissionId}&quoteTemplateId=${quoteTemplateId}`;
                    navigate(intakeUrl);
                    return intakeStatusResp?.data?.DraftFormSubmissionId;
                } else if (isBlockingIntakeStatus(intakeStatusResp.data.Status)) {
                    setIntakeFormStatus(intakeStatusResp.data);
                    setTextLoading(false);
                }
            } catch (error: unknown) {
                const apiError = extractApiError(error);
                const errorLanguage = getApiErrorLanguage(apiError);
                if (errorLanguage) {
                    setStatusLanguage(errorLanguage);
                }
                setIntakeFormStatus({
                    Status: getApiErrorStatus(apiError) || 'Failed',
                    Message: getApiErrorMessage(apiError),
                });
                setTextLoading(false);
                setVerifyOpen(false);
                return;
            }
        }

        if (draftFormSubmissionId) {
            const isValid = await validateStatusAndUpdate();
            if (!isValid) {
                setVerifyOpen(false);
                return;
            }
        }

        setIsModalVerified(true);
        if (!verifyEmail) {
            setLoading(false);
            return;
        }
        setLoading(true);

        try {
            const lockResult = await acquireLock(verifyEmail);
            if (!lockResult.success) {
                setLoading(false);
                setIsModalVerified(false);
                return;
            }

            const retainerResult = await checkRetainerRequired(lockResult.lockInfo, lockResult.contactId);
            if (retainerResult.requiresSignature) {
                setVerifyOpen(false);
                return;
            }

            setVerifyOpen(false);
            await fetchAndSetIntakeFormData(prospectId as string);
        } catch (error: unknown) {
            console.error('Form lock acquisition failed:', error);
        } finally {
            setLoading(false);
            setIsModalVerified(false);
        }
    };

    //This function to get the post submitted intake form
    const getPostSubmittedIntakeForm = async () => {
        if (formSubmissionId) {
            try {
                const statusResp = await getPostIntakeFormStatus(formSubmissionId);
                if (statusResp?.data?.Language) {
                    setStatusLanguage(statusResp.data.Language);
                }
            } catch (error: unknown) {
                const apiError = extractApiError(error);
                const errorLanguage = getApiErrorLanguage(apiError);
                if (errorLanguage) {
                    setStatusLanguage(errorLanguage);
                }
                if (getApiErrorStatus(apiError) === 'Forbidden') {
                    setVerifyOpen(false);
                    setIntakeFormStatus({
                        Status: getApiErrorStatus(apiError) || 'Failed',
                        Message: getApiErrorMessage(apiError),
                    });
                }
                return;
            }
        }

        setIsModalVerified(true);
        if (!verifyEmail) return;

        const payload = {
            prospectVersionId: prospectVersionId as number,
            formSubmissionId: formSubmissionId as string,
            clientEmail: verifyEmail,
        };
        setLoading(true);

        const lockResult = await acquireLock(verifyEmail);
        if (!lockResult.success) {
            setLoading(false);
            setIsModalVerified(false);
            return;
        }

        const retainerResult = await checkRetainerRequired(lockResult.lockInfo, lockResult.contactId);
        if (retainerResult.requiresSignature) {
            setVerifyOpen(false);
            setLoading(false);
            setIsModalVerified(false);
            return;
        }

        try {
            const resp = await getClientPostSubmittedIntakeFormData(payload);
            setContactResponses(resp?.data?.contactResponses ?? []);
            setSavedContactFormValues(resp.data.contactFormValues ?? []);
            setPostIntakeData(resp.data);
            // Initialize form values from post-intake submission data
            if (resp.data.formSubmissionFields) {
                setFormValues(initFormValuesFromSubmission(resp.data.formSubmissionFields));
            }
            setVerifyOpen(false);
            if (resp.status === 200) {
                fetchIntakeTemplateData();
            }
        } catch (error: unknown) {
            const apiError = extractApiError(error);
            if (apiError.status === 403) {
                errorSnackbar(t.notifications.intakeAlreadySubmitted);
            }
            console.error('Get post-submitted intake form data failed:', error);
        } finally {
            setLoading(false);
            setIsModalVerified(false);
        }
    };

    const fetchIntakeTemplateData = async () => {
        if (!intakeTemplateId) return;
        try {
            setIntakeFormLoading(true);
            try {
                const resp = await getClientSideIntakeForms(intakeTemplateId, quoteTemplateId);
                setHeaderDescription(resp.data.headerDescription ?? '');
                setFooterTemplate(resp.data.footerTemplate ?? '');
                setTemplateMetadata(resp.data.metadata ?? null);
                // Group fields by formGroupTitle
                const fieldsByGroup = groupTemplateFormFields(resp.data.templateFormFields);

                reportFormGroupMismatches('client-intake-form', resp.data.templateFormId, resp.data.formGroups, resp.data.templateFormFields);

                // If formGroups is defined, use it to order the groups (like the builder does)
                if (resp.data.formGroups && resp.data.formGroups.length > 0) {
                    // Add any group a field uses that the list is missing, or its fields would never be shown.
                    // Fields are grouped by their formGroupTitle column here, so resolve against that alone
                    const resolvedGroups = resolveFormGroups(
                        resp.data.formGroups,
                        resp.data.templateFormFields.map(({ formGroupTitle }) => ({ formGroupTitle })),
                    );
                    const orderedGroups = [...resolvedGroups].sort((a, b) => a.displayOrder - b.displayOrder);
                    setFormGroups(orderedGroups);
                    setSteps(orderedGroups.map((g) => g.name));
                    // Ensure all groups from formGroups have an entry, even if empty
                    const completeGroupedFields = { ...fieldsByGroup };
                    for (const group of orderedGroups) {
                        if (!(group.name in completeGroupedFields)) {
                            completeGroupedFields[group.name] = [];
                        }
                    }
                    setGroupedFields(completeGroupedFields);
                } else {
                    // Fallback: use groups derived from fields (order not guaranteed)
                    setFormGroups([]);
                    setSteps(Object.keys(fieldsByGroup));
                    setGroupedFields(fieldsByGroup);
                }
            } catch (error: unknown) {
                const apiError = extractApiError(error);
                const errorMessage = getApiErrorMessage(apiError);
                setError(errorMessage);
                setCanRetryError(isRetryableClientLoadError(error));
                if (!errorMessage.includes('expired')) {
                    errorSnackbar(errorMessage, true);
                }
                throw error;
            } finally {
                setIntakeFormLoading(false);
            }
        } catch (error: unknown) {
            console.error('Fetch intake template data failed:', error);
        }
    };

    const handleRetryTemplateLoad = () => {
        setError(null);
        setCanRetryError(false);
        void fetchIntakeTemplateData();
    };

    const handleSubmit = async () => {
        const duplicateEmailValidation = applyDuplicateContactEmailErrors();
        if (!duplicateEmailValidation.isValid) {
            setExpandedContactSections((prev) => {
                const next = { ...prev };
                for (const index of duplicateEmailValidation.contactErrorIndices) {
                    next[index] = true;
                }
                return next;
            });
            window.setTimeout(scrollToFirstError, 500);
            warningSnackbar(t.contactInfo.validation.duplicateEmailSnackbar, true);
            setIsSubmitDisabled(false);
            return;
        }

        // Validate form status based on entry mode before submitting
        // For postIntake, only block on Forbidden status (allow resubmission in other cases)
        const isValid = await validateStatusAndUpdate({ blockPostIntakeOnForbiddenOnly: true });
        if (!isValid) {
            if (entryMode.type === 'draft') {
                setVerifyOpen(false);
            }
            return;
        }

        // Build payload with entry mode-specific fields
        const payload: FormSubmissionRequest = {
            submittedBy: resolvedClientEmail,
            templateFormId: intakeTemplateId as string,
            templateFormFields: buildTemplateFormFields(formValues, groupedFields, { omitHiddenConditionalAnswers: true }),
            contactFormValues: contactFormValues.map(toContactFormValuesApi),
            draftFormSubmissionId: effectiveDraftFormSubmissionId,
            formSubmissionId: formSubmissionId ?? undefined,
            prospectVersionId: prospectVersionId ?? undefined,
            prospectId: postIntakeData?.prospectId || prospectId || undefined,
            instanceId: instanceId ?? undefined,
            contacts: contactResponses?.length > 1 ? contactResponses : undefined,
        };

        // Submit using the appropriate API for the entry mode
        try {
            if (prospectId && instanceId) {
                await clientSideOrchestrationFormSubmit(instanceId, prospectId, payload);
            } else {
                switch (entryMode.type) {
                    case 'postIntake':
                        await clientSidePostIntakeSubmission(payload);
                        break;
                    case 'manualProspect':
                    case 'draft':
                    case 'fresh':
                        await clientSideIntakeOnlyFormSubmit(payload);
                        break;
                }
            }
            if (isFinalStep) {
                setIsSubmitted(true);
            }
            successSnackbar(t.notifications.formSubmitted, true);
            setHasUnsavedChanges(false);
            // Release once the client is done so the firm can edit contacts. A failed submit keeps the lock.
            if (isFinalStep && contactResponses.length > 0) {
                await releaseLock();
            }
        } catch (error: unknown) {
            setIsSubmitDisabled(false);
            console.error('Form submission failed:', error);

            const apiError = extractApiError(error);
            if (isAxiosError(error)) {
                setIntakeFormStatus({
                    Status: getApiErrorStatus(apiError) || 'Failed',
                    Message: getApiErrorMessage(apiError),
                });
                return;
            }
            if (apiError.status === 403) {
                errorSnackbar(t.notifications.draftAlreadySubmitted);
            } else {
                errorSnackbar(t.notifications.somethingWentWrong, true);
            }
        }
    };

    const getIntakeStatus = async () => {
        if (!draftFormSubmissionId) return;
        setLoading(true);
        try {
            const statusResp = await getIntakeOnlyFormStatus(draftFormSubmissionId);
            if (statusResp?.data?.Language) {
                setStatusLanguage(statusResp.data.Language);
            }
            await fetchIntakeFormData();
            setLoading(false);
        } catch (error: unknown) {
            console.error('Get intake status failed:', error);
            const apiError = extractApiError(error);
            const errorLanguage = getApiErrorLanguage(apiError);
            if (errorLanguage) {
                setStatusLanguage(errorLanguage);
            }
            await fetchIntakeTemplateData();
            setIntakeFormStatus({
                Status: getApiErrorStatus(apiError) || 'Failed',
                Message: getApiErrorMessage(apiError),
            });
            setLoading(false);
            setVerifyOpen(false);
            setTextLoading(false);
        }
    };
    const getPostIntakeStatus = async () => {
        if (!formSubmissionId) return;
        setLoading(true);
        try {
            const statusResp = await getPostIntakeFormStatus(formSubmissionId);
            if (statusResp?.data?.Language) {
                setStatusLanguage(statusResp.data.Language);
            }
            fetchIntakeFormData();
            setLoading(false);
        } catch (error: unknown) {
            console.error('Get post-intake status failed:', error);
            const apiError = extractApiError(error);
            const errorLanguage = getApiErrorLanguage(apiError);
            if (errorLanguage) {
                setStatusLanguage(errorLanguage);
            }
            fetchIntakeTemplateData();
            setIntakeFormStatus({
                Status: getApiErrorStatus(apiError) || 'Failed',
                Message: getApiErrorMessage(apiError),
            });
            setTextLoading(false);
            setLoading(false);
        }
    };

    const fetchIntakeFormStatus = async () => {
        if (instanceId && prospectId) {
            setLoading(true);
            try {
                const intakeStatusResp = await getIntakeFormStatus(instanceId, prospectId);
                if (intakeStatusResp?.data?.Language) {
                    setStatusLanguage(intakeStatusResp.data.Language);
                }
                if (intakeStatusResp?.data?.DraftFormSubmissionId) {
                    const intakeUrl = `/intake/view/${intakeTemplateId}/${lawFirmId}?instanceId=${instanceId}&prospectId=${prospectId}&draftFormSubmissionId=${intakeStatusResp?.data?.DraftFormSubmissionId}&quoteTemplateId=${quoteTemplateId}`;
                    navigate(intakeUrl);
                    return;
                } else if (isBlockingIntakeStatus(intakeStatusResp.data.Status)) {
                    setIntakeFormStatus(intakeStatusResp.data);
                    setTextLoading(false);
                }
                setLoading(false);
                return intakeStatusResp?.data;
            } catch (error: unknown) {
                console.error('fetchIntakeFormStatus failed:', error);
                const apiError = extractApiError(error);
                const errorLanguage = getApiErrorLanguage(apiError);
                if (errorLanguage) {
                    setStatusLanguage(errorLanguage);
                }
                const errObj = {
                    Status: getApiErrorStatus(apiError) || 'Failed',
                    Message: getApiErrorMessage(apiError),
                };
                setIntakeFormStatus(errObj);
                setTextLoading(false);
                return errObj;
            } finally {
                setLoading(false);
            }
        }
    };

    const fetchAndSetIntakeFormData = async (prospectId: string) => {
        try {
            const quoteFormResponse = await getIntakePreFillData(prospectId);
            setContactResponses(quoteFormResponse?.data?.contactResponses ?? []);
            const combinedFormData = parseIntakePrefillResponse(quoteFormResponse?.data);
            setPrefillData(buildDefaultValuesFromQuote(combinedFormData));
            await fetchIntakeTemplateData();
        } catch (error: unknown) {
            console.error('Fetch quote form data failed:', error);
        }
    };

    const fetchIntakeFormData = async (statusData?: unknown) => {
        try {
            // Modes that require email verification show the verify modal
            // Fresh mode skips verification and loads the template directly
            if (requiresEmailVerification(entryMode) || (entryMode.type === 'manualProspect' && statusData)) {
                setVerifyOpen(true);
            } else {
                await fetchIntakeTemplateData();
            }
        } catch (error: unknown) {
            console.error('Fetch quote form data failed:', error);
        }
    };

    // This function can be called to attempt a navigation
    // This effect sets up the beforeunload event listener
    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (hasUnsavedChanges) {
                e.preventDefault();
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        // Clean up the event listener
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [hasUnsavedChanges]);

    useEffect(() => {
        switch (entryMode.type) {
            case 'manualProspect':
                fetchIntakeFormStatus().then((status) => {
                    if (isBlockingIntakeStatus(status?.Status)) return;
                    fetchIntakeFormData(status);
                });
                break;
            case 'draft':
                getIntakeStatus().catch(() => fetchIntakeFormData());
                break;
            case 'postIntake':
                getPostIntakeStatus().catch(() => fetchIntakeFormData());
                break;
            case 'fresh':
                fetchIntakeFormData();
                break;
        }
    }, [entryMode]); // oxlint-disable-line react-hooks/exhaustive-deps -- Form entry is messy - unavoidable for now

    useEffect(() => {
        if (Object.keys(groupedFields).length === 0 || Object.keys(prefillData).length === 0) {
            return;
        }

        const prefillByTag = buildPrefillByTag(prefillData);
        if (Object.keys(prefillByTag).length === 0) {
            return;
        }

        setFormValues((prev) => applyTagPrefill(prev, groupedFields, prefillByTag));
    }, [groupedFields, prefillData]);

    if (error) {
        return <ClientErrorPage message={error} onRetry={canRetryError ? handleRetryTemplateLoad : undefined} isRetrying={intakeFormLoading} language={language} />;
    }

    return (
        <ClientViewWrapper id={wrapperElementId} loading={intakeFormLoading}>
            {isBlockingIntakeStatus(intakeFormStatus?.Status) ? (
                <Box sx={mainWrapperStyles}>
                    <FormHeaderSection
                        lawFirmId={lawFirmId}
                        loading={loading || textLoading}
                        statusTitle={
                            intakeFormStatus?.Status && intakeFormStatus.Status in t.intakeStatus.statuses
                                ? t.intakeStatus.statuses[intakeFormStatus.Status as keyof typeof t.intakeStatus.statuses].title
                                : t.intakeStatus.fallbacks.failedTitle
                        }
                        statusMessage={
                            intakeFormStatus?.Status && intakeFormStatus.Status in t.intakeStatus.statuses
                                ? t.intakeStatus.statuses[intakeFormStatus.Status as keyof typeof t.intakeStatus.statuses].message
                                : t.intakeStatus.fallbacks.somethingWentWrong
                        }
                        statusType={intakeFormStatus?.Status}
                        showLawyerInfo
                        language={language}
                    />
                </Box>
            ) : (
                <Box sx={mainWrapperStyles}>
                    {!isSubmitted && (
                        <Box sx={{ width: '100%', overflow: 'hidden' }}>
                            <IntakeFormStepper steps={steps} currentStep={currentStep} />
                        </Box>
                    )}
                    <FormHeaderSection
                        lawFirmId={lawFirmId}
                        isSubmitted={isSubmitted}
                        isDraftSubmitDisabled={!isDraftSubmitEnabled}
                        isDraftFormSubmitted={isDraftFormSubmitted}
                        formSubmissionId={formSubmissionId}
                        matterType={matterType}
                        inactiveTimerReached={inactiveTimerReached}
                        inactiveSaveFailed={inactiveSaveFailed}
                        headerDescription={headerDescription}
                        footerTemplate={footerTemplate}
                        intakeUrl={intakeUrl}
                        language={language}
                    />
                    {groupedFields[steps[currentStep]] && !isSubmitted && (
                        <Stack
                            sx={{
                                borderRadius: '8px',
                                border: `1px solid ${theme.palette.neutral.borderColor}`,
                                backgroundColor: 'neutral.white',
                                padding: '8px',
                                height: 'fit-content',
                            }}
                        >
                            <Stack>
                                <Typography sx={{ margin: isMobile ? '8px' : '24px' }} variant={isMobile ? 'h3' : 'h2'}>
                                    {steps[currentStep]}
                                </Typography>
                                <SectionDescription description={currentStepGroupDescription} sx={sectionDescriptionSx} />
                                {currentStep === 0 && (
                                    <Stack spacing={3} sx={{ p: isMobile ? 1 : 2 }}>
                                        {contactFormValues.map((values, index) => (
                                            <Stack
                                                key={index}
                                                {...(hasContactFormErrors(contactFormErrors[index]) && {
                                                    'data-is-error': -1000 + index,
                                                })}
                                            >
                                                <ContactInfoSection
                                                    index={index}
                                                    totalContacts={contactFormValues.length}
                                                    values={values}
                                                    errors={contactFormErrors[index]}
                                                    onChange={(field, value) => handleContactFormChange(index, field, value)}
                                                    lawFirmId={lawFirmId}
                                                    hiddenFields={templateMetadata?.hiddenContactFields}
                                                    requiredFields={templateMetadata?.requiredContactFields}
                                                    fieldLabels={templateMetadata?.contactFieldLabels}
                                                    description={templateMetadata?.contactSectionDescription}
                                                    language={language}
                                                    expanded={expandedContactSections[index] ?? true}
                                                    onExpandedChange={(isExpanded) => setExpandedContactSections((prev) => ({ ...prev, [index]: isExpanded }))}
                                                    disabledFields={{
                                                        email: !!contactResponses[index]?.contactEmail,
                                                    }}
                                                />
                                            </Stack>
                                        ))}
                                    </Stack>
                                )}
                                {getVisibleFieldsForStep(currentStep).map((field) => (
                                    <Stack
                                        key={field.templateFormFieldId}
                                        {...(fieldErrors[field.templateFormFieldId] && {
                                            'data-is-error': field.questionSchema.order ?? field.displayOrder ?? true,
                                        })}
                                    >
                                        <FormComponent
                                            fieldObj={field}
                                            lawFirmId={lawFirmId as string}
                                            language={language}
                                            isError={!!fieldErrors[field.templateFormFieldId]}
                                            helper={fieldErrors[field.templateFormFieldId] ?? undefined}
                                            tabindex={field.questionSchema.order}
                                            value={formValues[field.templateFormFieldId] ?? field.questionSchema.value}
                                            entryErrors={contactBlockErrors[field.templateFormFieldId]}
                                            onFieldValueChange={(_name: string, value: unknown) => {
                                                handleFieldValueChange(field, value);
                                            }}
                                        />
                                    </Stack>
                                ))}
                            </Stack>
                            {isFinalStep && practiceArea === 'WillsAndEstates' && templateMetadata?.showFinancialSummary !== false && (
                                <Box sx={{ px: 3, py: 1.5 }}>
                                    <FinancialSummary groupedFields={groupedFields} formValues={formValues} language={language} />
                                </Box>
                            )}
                            {isFinalStep && contactResponses?.length > 0 && retainerResponse?.length > 0 && <RetainerAcceptanceTable rows={retainerResponse || []} language={language} />}
                            <ButtonSection
                                currentStep={currentStep}
                                numSteps={steps.length}
                                formSubmissionId={formSubmissionId}
                                isDraftSubmitDisabled={(contactResponses?.length > 0 && loading && isDraftFormSubmitted) || !isDraftSubmitEnabled}
                                isNotAllRetainersSigned={entryMode.type !== 'postIntake' && isFinalStep && contactResponses?.length > 0 && retainerResponse?.length > 0 && !areAllRetainersSigned}
                                isSubmitDisabled={!isDraftSubmitEnabled || isSubmitDisabled}
                                handleDraftSubmit={handleDraftSubmit}
                                handleBackClick={handleBack}
                                handleNextClick={handleNext}
                                handleSubmitClick={() => {
                                    if (!validateCurrentStep()) return;
                                    setIsSubmitDisabled(true);
                                    handleSubmit();
                                }}
                                language={language}
                            />
                        </Stack>
                    )}
                </Box>
            )}
            <VerifyEmailDialog
                open={verifyOpen}
                loading={loading}
                verifyEmail={verifyEmail}
                isModalVerified={isModalVerified}
                isIntakeForm={entryMode.type === 'postIntake' || entryMode.type === 'manualProspect'}
                onClose={() => setVerifyOpen(false)}
                onEmailChange={setVerifyEmail}
                language={language}
                onVerify={() => {
                    switch (entryMode.type) {
                        case 'postIntake':
                            getPostSubmittedIntakeForm();
                            break;
                        case 'manualProspect':
                            getContactIdForIntakeForm();
                            break;
                        case 'draft':
                            // Check for locking if there's a prospect associated with this draft
                            handleVerifyClientOnRevisit(!!entryMode.prospectId);
                            break;
                        case 'fresh':
                            // Fresh mode doesn't require verification, but handle defensively
                            break;
                    }
                }}
            />
            {showLegalDocument && lockInfo?.globalRetainerFileId && resolvedProspectId && (
                <LegalDocumentModal
                    fileId={lockInfo.globalRetainerFileId}
                    documentDisplayName={lockInfo.documentDisplayName}
                    contactId={contactId}
                    contactEmail={verifyEmail}
                    prospectId={resolvedProspectId}
                    initialClientName={clientName}
                    onComplete={handleLegalDocumentComplete}
                    onClose={closeLegalDocument}
                    language={language}
                />
            )}
            {contactResponses?.length > 1 && !isSubmitted && ((!isDraftFormSubmitted && prospectId) || (formSubmissionId && prospectVersionId)) && (
                <ActivityTracker timeout={ACTIVITY_TRACKER_TIMEOUT_MS} warningTime={ACTIVITY_TRACKER_WARNING_TIME_MS} onTimeout={handleInactivityTimeout} language={language} />
            )}
        </ClientViewWrapper>
    );
}
